/*
  Таблица «Должники» экрана обзвона: что показать, что найдено, кто выбран.

  Выбор хранится строками, а не номерами строк таблицы, и переживает смену
  переключателя и поиск: человек ищет фамилию, отмечает, ищет следующую - и
  ждёт, что набранные раньше никуда не делись. Порядок выбора сохраняется: в
  нём же обзвон и наберёт («в том порядке, в каком их выбрал человек»).

  Поиск уходит на сервер с задержкой: запрос на каждую букву при пяти тысячах
  счетов - лишняя работа, а ответы, пришедшие не по порядку, показали бы
  таблицу для «Ив» после таблицы для «Иванов». Устаревший ответ отбрасывается.
*/

import { useCallback, useEffect, useState } from 'react'

import { api } from '../api/client.js'
import useServerPaging from './useServerPaging.js'

export const SEARCH_DELAY_MS = 300

/*
  Страницы режет сервер (21.10.2026, требование владельца: «пагинация должна быть серверной,
  а не на фронте»): хук просит окно (`limit`, `offset`) вместе с частью списка и поиском, а
  сервер отбирает и ищет по ВСЕМУ списку и отвечает страницей и общим числом. Прежнего
  «загружены первые 500, остальных найдёт поиск» больше нет: до любого должника можно долистать.
  Смена части списка и новый поиск возвращают на первую страницу; перечитывание после
  обзвона остаётся на текущей.
*/
export default function useDebtorList() {
  const [view, setViewState] = useState('all')
  const [typed, setTyped] = useState('')
  const [search, setSearch] = useState('')
  const [offset, setOffset] = useState(0)
  const [version, setVersion] = useState(0)
  const [state, setState] = useState({ list: null, error: null })
  const [chosen, setChosen] = useState([])
  const total = state.list?.total ?? 0
  const { size, bar } = useServerPaging(offset, total, setOffset)

  const setView = useCallback((next) => {
    setViewState(next)
    setOffset(0)
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => setSearch(typed.trim()), SEARCH_DELAY_MS)
    return () => clearTimeout(timer)
  }, [typed])

  // Новый поиск - другой список: читать его начинают с первой страницы. Сброс идёт только
  // при ИЗМЕНЕНИИ запроса: сброс «по таймеру» отбрасывал бы человека на первую страницу,
  // если он успел перелистнуть раньше, чем сработала задержка поиска.
  const [searched, setSearched] = useState('')
  if (searched !== search) {
    setSearched(search)
    setOffset(0)
  }

  useEffect(() => {
    let current = true
    api
      .callList({ view, search, limit: size, offset })
      .then((list) => {
        if (!current) return
        // Страница оказалась за концом (отметили последнего должника страницы):
        // переходим на последнюю существующую, а не показываем пустое окно.
        if (list.rows.length === 0 && list.total > 0 && offset > 0) {
          setOffset(Math.floor((list.total - 1) / size) * size)
          return
        }
        setState({ list, error: null })
      })
      .catch((error) => current && setState((now) => ({ ...now, error })))
    return () => {
      current = false
    }
  }, [view, search, size, offset, version])

  const reload = useCallback(() => setVersion((now) => now + 1), [])

  const toggle = useCallback(
    (row) =>
      setChosen((now) =>
        now.some((item) => item.account_id === row.account_id)
          ? now.filter((item) => item.account_id !== row.account_id)
          : [...now, row],
      ),
    [],
  )

  /*
    «Выбрать все» берёт ровно показанные строки - то же правило, что в
    конвейере (решение владельца 09.09.2026): отмеченное раньше остаётся, а
    повторное нажатие снимает отметку только с показанных.
  */
  const toggleAll = useCallback((rows, on) => {
    const shown = new Set(rows.map((row) => row.account_id))
    setChosen((now) =>
      on
        ? [...now, ...rows.filter((row) => !now.some((item) => item.account_id === row.account_id))]
        : now.filter((item) => !shown.has(item.account_id)),
    )
  }, [])

  const clear = useCallback(() => setChosen([]), [])

  /*
    «Выбрать весь отбор» (решение владельца 23.10.2026): должники нынешней части списка и
    поиска с сервера - с разбором «можно звонить», по которому окно обзвона делит выбранных.
    Добавляются к выбору в порядке таблицы, уже выбранные не повторяются.
  */
  const fetchAll = useCallback(() => api.callListSelection({ view, search }), [view, search])
  const addAll = useCallback((rows) => toggleAll(rows, true), [toggleAll])

  return {
    view,
    setView,
    bar,
    typed,
    setTyped,
    list: state.list,
    error: state.error,
    chosen,
    toggle,
    toggleAll,
    clear,
    reload,
    filterKey: `${view}|${search}`,
    fetchAll,
    addAll,
  }
}
